import {
  Button,
  ErrorIcon,
  SnackbarManager,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@cmg/design-system';
import { format, parseISO } from 'date-fns';
import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { getEnvVars } from '../../../../../../common/config/appSettings';
import { AttestationForm, AttestationFormStatus } from '../../../../../../graphql';
import { isAttestationStatusValid } from '../../../../../order-book/attestation/Attestation.model';
import AttestationFormStatusRenderer from '../../../../../order-book/attestation/AttestationFormStatusRenderer';
import { useComplianceContext } from '../../context/ComplianceContext';
import {
  CertificateTypeLabels,
  getAttestationStatus,
} from '../../context/hooks/certificates/Certificates.model';
import { Attestation } from '../../context/hooks/certificates/useCertificatesModel';
import CertificateDownloadLink from './CertificateDownloadLink';

export type Props = {
  attestation: Attestation;
  attestationForm: AttestationForm | undefined | null;
};

export const CertificateTableRow: React.FC<Props> = ({ attestation, attestationForm }) => {
  const { signatoryDate = '', expirationDate = '' } = attestationForm ?? {};
  const { tooltipMessage } = useComplianceContext().certificates.warning;
  const isStatusValid = isAttestationStatusValid(getAttestationStatus(attestation)!);
  const { cmgEntityKey } = useComplianceContext().certificates.attestation!;
  const certificateType = useComplianceContext().certificates.certificateType;

  const { attestationCopyLinkUrl } = getEnvVars();

  const handleCopy = () => {
    SnackbarManager.success('Link copied to clipboard.');
  };

  const copyContent = attestationCopyLinkUrl
    ? attestationCopyLinkUrl
    : 'Attestation link not available in this environment';

  return (
    <TableRow aria-label="get certificate table row">
      <TableCell>
        {!isStatusValid && (
          <Tooltip title={tooltipMessage} variant="error" placement="top">
            <ErrorIcon fontSize="small" color="error" />
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        {certificateType
          ? CertificateTypeLabels[certificateType]
          : CertificateTypeLabels.FORM51305131}
      </TableCell>
      <TableCell>
        <AttestationFormStatusRenderer status={getAttestationStatus(attestation)!} />
      </TableCell>
      <TableCell>{signatoryDate ? format(parseISO(signatoryDate), 'MM/dd/yyyy') : ''}</TableCell>
      <TableCell data-testid="certificate-expiration-date">
        <Typography
          color={theme =>
            getAttestationStatus(attestation) === AttestationFormStatus.Expired
              ? theme.palette.error.main
              : ''
          }
        >
          {expirationDate ? format(parseISO(expirationDate), 'MM/dd/yyyy') : ''}
        </Typography>
      </TableCell>
      <TableCell>
        {!isStatusValid && (
          <CopyToClipboard text={copyContent} onCopy={handleCopy}>
            <Tooltip title="Copy link to clipboard" variant="info" placement="top">
              <Button variant="text">Copy link</Button>
            </Tooltip>
          </CopyToClipboard>
        )}
        {isStatusValid && <CertificateDownloadLink cmgEntityKey={cmgEntityKey} />}
      </TableCell>
    </TableRow>
  );
};
